/**
 * The city hub's data (web plan W3.2): the backend's `cityPage` (B3.7b) for a
 * route slug (@/seo/cityHubs `cityHubKey`), in the route's locale. The SEO
 * server sends the same document with the same variables (server/routes.mjs),
 * so the head it builds is the one this page shows.
 *
 * Status like a detail page (@/utils/detailStatus, web plan W0.2): a slug no
 * city can have, a NOT_FOUND answer (unknown city, inactive city, another
 * country) or a null result is `not_found`; 429 / 5xx / network is `error`
 * (robots untouched).
 */
import { CITY_HUB_PAGE } from '@/graphql/queries';
import { usePreloadedQuery } from '@/hooks/routePreload';
import { cityPageVariables, type CityHubPage } from '@/seo/cityHubs';
import type { Locale } from '@/seo/locales';
import { detailStatus, type DetailStatus } from '@/utils/detailStatus';
import { queryErrorMessage } from './useAppData';

export interface CityPageState {
  hub: CityHubPage | null;
  status: DetailStatus;
  error: string | null;
  refetch: () => void;
}

export function useCityPage(key: string | null, locale: Locale): CityPageState {
  // The route module's server copy answers first (web plan W4.5).
  const { data, loading, error, refetch } = usePreloadedQuery<{ cityPage?: CityHubPage | null }>(CITY_HUB_PAGE, {
    skip: !key,
    variables: key ? cityPageVariables(key, locale) : undefined,
    // Retry goes back through `loading` instead of leaving the error up.
    notifyOnNetworkStatusChange: true,
  });
  const hub = data?.cityPage ?? null;
  return {
    hub,
    status: detailStatus({ item: hub, loading, error, validParam: Boolean(key) }),
    error: queryErrorMessage(error),
    refetch: () => {
      void refetch();
    },
  };
}
